import React from 'react';
import { Modal, Menu, Divider } from 'antd';
import { Link } from 'react-router-dom';
import { SidebarIcon } from '../../../../core/utils/general';
import { subRoutes, subDivisions } from '../../../../core/utils/constants/constants';
import { determineUserAccess } from '../../../../core/permissions/userLevelAccess';
import { CloseOutlined } from '@ant-design/icons';
import OrgDisplay from '../../../shared/OrgDisplay';

const { Item, SubMenu, ItemGroup } = Menu;

const transformFeatureName = (feature) => {
  switch (feature) {
    case 'DEMOGRAPHIC':
      return 'Demograph';
    case 'AUDIENCES':
      return 'Audiences';
    case 'LOCATIONS':
      return 'Locations';
    case 'ADVERTISER':
      return 'Advertiser';
    case 'PUBLISHER':
      return 'Publisher';
    case 'SIGNAL TAGS':
      return 'Signal Tags';
    case 'INVOICES':
      return 'Invoices';
    case 'AUDIENCE':
      return 'Audience';
    case 'TARGETING':
      return 'Targeting';
    case 'TAGS':
      return 'Tags';
    case 'CREATIVES':
      return 'Creatives';
    case 'RETAILS_STORES':
      return 'Retail Stores';
    case 'SETTINGS':
      return 'Settings';
    case 'KNOWLEDGE_BASE':
      return 'Knowledge Base';
    case 'ORGS':
      return 'Organization';
    case 'TASKS':
      return 'Tasks';
    case 'CREATE_TASK':
      return 'Create Task';
    case 'SEO':
      return 'Seo Domains';
    default:
      return feature;
  }
};

const accessibleFeatures = (
  allFeatures,
  features,
  hasAccessAll = false,
  orgLevel,
  urlLink,
  parentOrgLevel,
  billing
) => {
  return allFeatures
    .filter((feature) => {
      if (hasAccessAll) return true;
      return features.indexOf(feature) > -1;
    })
    .map((feature, i) => {
      if (
        (feature === 'INVOICES' && !billing) ||
        feature === 'LIBRARY' ||
        feature === 'HOME' ||
        feature === 'USERS'
      ) {
        return null;
      }

      const keySubRoutes = Object.keys(subRoutes).find((key) => key === feature);
      
      return (
        <React.Fragment key={i}>
          {keySubRoutes ? (
            <SubMenu
              key={`${i}-submenu-${feature}`}
              icon={<SidebarIcon type={feature} />}
              title={transformFeatureName(feature)}
            >
              {subRoutes[keySubRoutes].map((subRoute) => {
                let subName = subRoute;
                switch (subRoute) {
                  case 'Paid Search Campaign':
                    subName = 'Campaign';
                    break;
                  case 'SEO Domains':
                    subName = 'Domains';
                    break;
                  case 'Paid Search Reports':
                    subName = 'Reports';
                    break;
                  case 'SEO Reports':
                    subName = 'Reports';
                    break;
                  default:
                }
                if (orgLevel === 'STANDARD' && subRoute === 'Overview') return null;

                return (
                  <Item key={subRoute}>
                    <Link to={`/${feature.toLowerCase()}`}>
                      <span>{subName}</span>
                    </Link>
                  </Item>
                );
              })}
            </SubMenu>
          ) : (
            <Item key={feature}>
              <Link to={`/${feature.toLowerCase()}`}>
                <SidebarIcon type={feature} />
                <span>{transformFeatureName(feature)}</span>
              </Link>
            </Item>
          )}
        </React.Fragment>
      );
    });
};

const MobileNavigationModal = ({ visible, onClose, currentUser, themeData }) => {
  const userPermission = determineUserAccess(currentUser.permission);
  const { isSelfService, billing } = userPermission;
  const { features, role } = currentUser;
  const orgLevel = role.org.level;
  
  // Get sidebar color from theme data or use default
  let sidebarColor = '#001529'; // Default dark blue
  if (themeData && themeData.getOrgThemeConfig) {
    sidebarColor = themeData.getOrgThemeConfig.sidebarColor || sidebarColor;
  }
  
  let urlLink;
  let parentOrgLevel = null;
  let parentOrg = role.org.parentOrg;

  if (parentOrg.length) {
    urlLink = parentOrg[parentOrg.length - 1].advertiserKnowledgeBaseURL;
    parentOrgLevel = parentOrg[parentOrg.length - 1].level;
  }

  const divisions = Object.keys(subDivisions);

  const renderSubCategoriesAndFeatures = () => {
    return divisions.map((division, i) => {
      if (division === 'ATTENTION SIGNALS') {
        const hasPermission = features.includes('EDITORIAL');
        if (!hasPermission) return null;
        return (
          <ItemGroup key={division} title={division}>
            {accessibleFeatures(
              subDivisions[division],
              features,
              true,
              orgLevel,
              urlLink,
              parentOrgLevel,
              billing
            )}
          </ItemGroup>
        );
      }

      if (division === 'LIBRARY') {
        if (!isSelfService) return null;
        return (
          <ItemGroup key={division} title={division}>
            {accessibleFeatures(
              subDivisions[division],
              features,
              true,
              orgLevel,
              urlLink,
              parentOrgLevel,
              billing
            )}
          </ItemGroup>
        );
      }

      if (division === 'TASK MANAGEMENT') {
        const hasPermission = features.includes('TASK_MANAGEMENT');
        if (!hasPermission) return null;
        return (
          <ItemGroup key={division} title={division}>
            {accessibleFeatures(
              subDivisions[division],
              features,
              true,
              orgLevel,
              urlLink,
              parentOrgLevel,
              billing
            )}
          </ItemGroup>
        );
      }

      if (division === 'ADMIN') {
        let subDiv = subDivisions[division];

        const hasPermission = features.includes('ORGS');

        if (!hasPermission) {
          if (
            (orgLevel === 'STANDARD' && urlLink && parentOrgLevel === 'CHANNEL_PARTNER') ||
            orgLevel === 'CHANNEL_PARTNER'
          ) {
            subDiv = ['KNOWLEDGE_BASE', 'SETTINGS'];
          } else {
            subDiv = ['SETTINGS'];
          }
        } else {
          if (
            (orgLevel === 'STANDARD' && urlLink && parentOrgLevel === 'CHANNEL_PARTNER') ||
            orgLevel === 'CHANNEL_PARTNER'
          ) {
            subDiv = ['KNOWLEDGE_BASE', 'SETTINGS', 'ORGS'];
          } else {
            subDiv = ['SETTINGS', 'ORGS'];
          }
        }

        if (!hasPermission) return null;
        return (
          <ItemGroup key={division} title={division}>
            {accessibleFeatures(
              subDiv,
              features,
              true,
              orgLevel,
              urlLink,
              parentOrgLevel,
              billing
            )}
          </ItemGroup>
        );
      }

      return division === 'MAIN' ? (
        <ItemGroup key={division} title={division}>
          {accessibleFeatures(
            subDivisions[division],
            features,
            false,
            orgLevel,
            urlLink,
            parentOrgLevel,
            billing
          )}
        </ItemGroup>
      ) : null;
    });
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white', backgroundColor: sidebarColor, padding: '16px 24px', margin: -24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <OrgDisplay imgKey={currentUser.role.org.logo ? currentUser.role.org.logo.key : null} collapsed={false} style={{ height: '32px', width: 'auto' }} />
          </div>
          <CloseOutlined 
            onClick={onClose} 
            style={{ cursor: 'pointer', fontSize: '16px', color: 'white' }}
          />
        </div>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      width="100%"
      style={{ 
        top: 0,
        margin: 0,
        padding: 0,
        maxWidth: '100%',
        height: '100vh',
        backgroundColor: sidebarColor
      }}
      bodyStyle={{ 
        padding: 0,
        height: 'calc(100vh - 55px)',
        overflow: 'auto',
        backgroundColor: sidebarColor
      }}
    >
      <Menu
        theme="dark"
        mode="inline"
        style={{ 
          border: 'none',
          height: '100%',
          backgroundColor: sidebarColor
        }}
        onClick={onClose}
      >
        <Item key="home">
          <Link to="/home">
            <span>Home</span>
          </Link>
        </Item>
        <Divider />
        {renderSubCategoriesAndFeatures()}
      </Menu>
    </Modal>
  );
};

export default MobileNavigationModal;
